import { Chip, Icon, IconButton, Slider, Stack, Text, tokens } from '@/design';

interface ThreeDControlsPanelProps {
  hasSelection: boolean;
  isolated: boolean;
  hidden: boolean;
  ghosted: boolean;
  sectionPlaneEnabled: boolean;
  clipBoxEnabled: boolean;
  exploded: number;
  lightingEnabled: boolean;
  shadowsEnabled: boolean;
  sunHour: number;
  sectionOffset: number;
  clipOffset: number;
  materialState: 'textured' | 'untextured' | 'missing-texture';
  canvasTone: 'light' | 'neutral-dark';
  canvasToneDisabled: boolean;
  onToggleIsolate(): void;
  onToggleHidden(): void;
  onToggleGhosted(): void;
  onToggleSectionPlane(): void;
  onToggleClipBox(): void;
  onChangeExploded(value: number): void;
  onToggleLighting(): void;
  onToggleShadows(): void;
  onChangeSunHour(value: number): void;
  onChangeSectionOffset(value: number): void;
  onChangeClipOffset(value: number): void;
  onToggleCanvasTone(): void;
}

export function ThreeDControlsPanel({
  hasSelection,
  isolated,
  hidden,
  ghosted,
  sectionPlaneEnabled,
  clipBoxEnabled,
  exploded,
  lightingEnabled,
  shadowsEnabled,
  sunHour,
  sectionOffset,
  clipOffset,
  materialState,
  canvasTone,
  canvasToneDisabled,
  onToggleIsolate,
  onToggleHidden,
  onToggleGhosted,
  onToggleSectionPlane,
  onToggleClipBox,
  onChangeExploded,
  onToggleLighting,
  onToggleShadows,
  onChangeSunHour,
  onChangeSectionOffset,
  onChangeClipOffset,
  onToggleCanvasTone,
}: ThreeDControlsPanelProps) {
  return (
    <div
      role="region"
      aria-label="3D controls"
      style={{
        position: 'absolute',
        left: tokens.space[2],
        bottom: tokens.space[2],
        zIndex: tokens.zIndex.panel,
        width: tokens.width.popoverLg,
        maxWidth: '100%',
        background: tokens.color.surface[1],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        padding: tokens.space[2],
      }}
    >
      <Stack direction="column" gap={2}>
        <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
          <IconButton
            aria-label="Isolate selected object (I)"
            aria-pressed={isolated}
            disabled={!hasSelection}
            onClick={onToggleIsolate}
            style={{ background: isolated ? tokens.color.surface[3] : 'transparent' }}
          >
            <Icon name="filter_center_focus" size={20} decorative />
          </IconButton>
          <IconButton
            aria-label="Hide selected object (H)"
            aria-pressed={hidden}
            disabled={!hasSelection}
            onClick={onToggleHidden}
            style={{ background: hidden ? tokens.color.surface[3] : 'transparent' }}
          >
            <Icon name="visibility_off" size={20} decorative />
          </IconButton>
          <IconButton
            aria-label="Ghost non-selected objects (G)"
            aria-pressed={ghosted}
            disabled={!hasSelection}
            onClick={onToggleGhosted}
            style={{ background: ghosted ? tokens.color.surface[3] : 'transparent' }}
          >
            <Icon name="blur_on" size={20} decorative />
          </IconButton>
          <IconButton
            aria-label="Toggle section planes (S)"
            aria-pressed={sectionPlaneEnabled}
            onClick={onToggleSectionPlane}
            style={{ background: sectionPlaneEnabled ? tokens.color.surface[3] : 'transparent' }}
          >
            <Icon name="splitscreen" size={20} decorative />
          </IconButton>
          <IconButton
            aria-label="Toggle clipping box (B)"
            aria-pressed={clipBoxEnabled}
            onClick={onToggleClipBox}
            style={{ background: clipBoxEnabled ? tokens.color.surface[3] : 'transparent' }}
          >
            <Icon name="crop_free" size={20} decorative />
          </IconButton>
        </Stack>

        <Stack direction="column" gap={1}>
          <Text as="label" variant="caption">
            Section plane offset
          </Text>
          <Slider
            min={-100}
            max={100}
            step={1}
            value={Math.round(sectionOffset)}
            aria-label="Section plane offset"
            onChange={onChangeSectionOffset}
          />
          <Text as="label" variant="caption">
            Clipping box offset
          </Text>
          <Slider
            min={-100}
            max={100}
            step={1}
            value={Math.round(clipOffset)}
            aria-label="Clipping box offset"
            onChange={onChangeClipOffset}
          />
        </Stack>

        <Stack direction="column" gap={1}>
          <Text as="label" variant="caption">
            Exploded view
          </Text>
          <Slider
            min={0}
            max={100}
            step={1}
            value={Math.round(exploded * 100)}
            aria-label="Exploded view factor"
            onChange={(next) => onChangeExploded(next / 100)}
          />
          <Chip
            icon="open_in_full"
            label={`${Math.round(exploded * 100)}%`}
            variant={exploded > 0 ? 'info' : 'neutral'}
          />
        </Stack>

        <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
          <IconButton
            aria-label="Toggle lighting"
            aria-pressed={lightingEnabled}
            onClick={onToggleLighting}
            style={{ background: lightingEnabled ? tokens.color.surface[3] : 'transparent' }}
          >
            <Icon name="light_mode" size={20} decorative />
          </IconButton>
          <IconButton
            aria-label="Toggle shadows"
            aria-pressed={shadowsEnabled}
            onClick={onToggleShadows}
            style={{ background: shadowsEnabled ? tokens.color.surface[3] : 'transparent' }}
          >
            <Icon name="wb_shade" size={20} decorative />
          </IconButton>
          <Chip
            icon={materialState === 'missing-texture' ? 'image_not_supported' : 'texture'}
            label={
              materialState === 'missing-texture'
                ? 'Missing texture'
                : materialState === 'untextured'
                  ? 'Untextured'
                  : 'Textured'
            }
            variant={materialState === 'missing-texture' ? 'warning' : 'neutral'}
          />
        </Stack>

        <Stack direction="column" gap={1}>
          <Text as="label" variant="caption">
            Sun position
          </Text>
          <Slider
            min={0}
            max={23}
            step={1}
            value={Math.round(sunHour)}
            aria-label="Sun hour"
            onChange={onChangeSunHour}
          />
          <Chip icon="wb_sunny" label={`${Math.round(sunHour)}:00`} variant="neutral" />
        </Stack>

        <Stack direction="row" align="center" gap={1}>
          <IconButton
            aria-label="Toggle 3D canvas tone"
            aria-pressed={canvasTone === 'neutral-dark'}
            disabled={canvasToneDisabled}
            onClick={onToggleCanvasTone}
          >
            <Icon name={canvasTone === 'neutral-dark' ? 'wb_shade' : 'light_mode'} size={20} decorative />
          </IconButton>
          <Text as="span" variant="caption">
            3D canvas: {canvasTone === 'neutral-dark' ? 'Neutral dark' : 'Light'}
            {canvasToneDisabled ? ' (token not configured)' : ''}
          </Text>
        </Stack>
      </Stack>
    </div>
  );
}
